import React from "react";
import { v5 as uuidv5 } from "uuid";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ApiError } from "../../../../api/apiFetch";
import { VideoPlatformResource } from "../../../../api/video_platform";

// Must match GCAL_MEETING_NAMESPACE in hosted_meeting_sync_service.ts
const GCAL_MEETING_NAMESPACE = "6ba7b814-9dad-11d1-80b4-00c04fd430c8";

interface Props {
  gcalEventId: string;
}

export const HostedMeetingFallbackSection: React.FC<Props> = ({ gcalEventId }) => {
  const hostedMeetingId = React.useMemo(
    () => uuidv5(gcalEventId, GCAL_MEETING_NAMESPACE),
    [gcalEventId]
  );

  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(false);

  const [fallbackUrlInput, setFallbackUrlInput] = React.useState("");
  const [savedFallbackUrl, setSavedFallbackUrl] = React.useState("");
  const [fallbackUrlDirty, setFallbackUrlDirty] = React.useState(false);
  const [useFallback, setUseFallback] = React.useState(false);

  const [isSavingUrl, setIsSavingUrl] = React.useState(false);
  const [isTogglingFallback, setIsTogglingFallback] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setFetchError(false);

    VideoPlatformResource.getMeetingCached(hostedMeetingId)
      .then((m) => {
        if (cancelled) return;
        setFallbackUrlInput(m.fallbackMeetUrl ?? "");
        setSavedFallbackUrl(m.fallbackMeetUrl ?? "");
        setUseFallback(m.useFallback ?? false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setFetchError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hostedMeetingId]);

  const handleSaveUrl = async () => {
    setIsSavingUrl(true);
    const urlToSave = fallbackUrlInput.trim() || null;

    try {
      const updates: { fallbackMeetUrl: string | null; useFallback?: boolean } = {
        fallbackMeetUrl: urlToSave,
      };

      if (!urlToSave && useFallback) {
        updates.useFallback = false;
        setUseFallback(false);
      }

      await VideoPlatformResource.updateMeeting(hostedMeetingId, updates);
      if (!mountedRef.current) return;
      setSavedFallbackUrl(urlToSave ?? "");
      setFallbackUrlDirty(false);
      setNotFound(false);
      setSnackbar({
        open: true,
        message: urlToSave ? "Google Meet URL saved" : "Google Meet URL removed",
        severity: "success",
      });
    } catch {
      if (!mountedRef.current) return;
      setSnackbar({
        open: true,
        message: "Failed to save Google Meet URL",
        severity: "error",
      });
    } finally {
      if (mountedRef.current) setIsSavingUrl(false);
    }
  };

  const handleToggleFallback = async () => {
    const newValue = !useFallback;
    setIsTogglingFallback(true);
    setUseFallback(newValue);

    try {
      await VideoPlatformResource.updateMeeting(hostedMeetingId, {
        useFallback: newValue,
      });
      if (!mountedRef.current) return;
      setSnackbar({
        open: true,
        message: newValue
          ? "Fallback active — users will be redirected to Google Meet"
          : "Fallback disabled — users join via Focused Space",
        severity: "success",
      });
    } catch {
      if (!mountedRef.current) return;
      setUseFallback(!newValue);
      setSnackbar({
        open: true,
        message: "Failed to update fallback",
        severity: "error",
      });
    } finally {
      if (mountedRef.current) setIsTogglingFallback(false);
    }
  };

  if (fetchError) {
    return (
      <>
        <Divider sx={{ my: 1 }} />
        <Alert severity="warning" sx={{ mt: 1 }}>
          Could not load Google Meet fallback settings. Check your connection and try again.
        </Alert>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Divider sx={{ my: 1 }} />
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Skeleton variant="text" width={160} height={24} />
          <Skeleton variant="text" width="70%" height={16} sx={{ mt: 0.5 }} />
          <Skeleton variant="rounded" height={40} sx={{ mt: 1.5 }} />
          <Skeleton variant="text" width={140} height={32} sx={{ mt: 1 }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: useFallback ? "warning.main" : "divider",
          borderRadius: 1,
          bgcolor: useFallback ? "warning.50" : "transparent",
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Google Meet Fallback
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: notFound ? 0.5 : 1.5 }}>
          Set a Google Meet URL as a backup. When activated, all users will be
          transparently redirected to Google Meet. The GCal event location will
          update automatically on the next sync.
        </Typography>
        {notFound && (
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1.5 }}>
            Meeting not yet synced — saving will initialize it automatically.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Google Meet URL"
            placeholder="https://meet.google.com/xxx-xxxx-xxx"
            value={fallbackUrlInput}
            onChange={(e) => {
              setFallbackUrlInput(e.target.value);
              setFallbackUrlDirty(e.target.value.trim() !== savedFallbackUrl);
            }}
            InputProps={
              fallbackUrlInput && !fallbackUrlDirty
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Open in new tab">
                          <IconButton
                            size="small"
                            href={fallbackUrlInput}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveUrl}
            disabled={!fallbackUrlDirty || isSavingUrl}
            sx={{ whiteSpace: "nowrap", minWidth: 64, height: 40 }}
          >
            {isSavingUrl ? <CircularProgress size={16} /> : "Save"}
          </Button>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={useFallback}
              onChange={handleToggleFallback}
              disabled={isTogglingFallback || !savedFallbackUrl}
              color="warning"
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>Use Fallback</span>
              {isTogglingFallback && <CircularProgress size={16} />}
            </Box>
          }
        />
        <FormHelperText>
          {useFallback
            ? "⚠️ Active — all users are being redirected to Google Meet"
            : savedFallbackUrl
            ? "Disabled — users join via Focused Space as normal"
            : "Save a Google Meet URL above to enable this toggle"}
        </FormHelperText>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
